import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Close } from '@videoblocks/react-icons';

import { ReduxState } from '../../../../../AppLoader/types';
import AiRiskModal from '../../../../../PopUps/components/AiRiskModal';
import { notifyError } from '../../../../../ReactToastify/containers/ContentEditToast';
import {
  selectUser,
  selectUserSubscriptionUniqueId,
} from '../../../../../auth/AuthSelectors';
import { ContentEditSegment } from '../../../../../auth/AuthTypes';
import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import { getPreviewUrl } from '../../../../../common/utils';
import ContentEditAPI from '../../../../ContentEdit/ContentEditAPI';
import {
  closeVideoEditorModal,
  resetModalState,
  selectContentEditSegment,
  selectCreditBalance,
  selectIsSpendingCredits,
  selectModalState,
  selectShouldShowAIEditFeatures,
  selectVideoEditorModalIsOpen,
  selectVideoEditorModalStockItem,
  spendCredits,
  startPolling,
  selectOptIns,
  TaskStatus,
  selectIsPolling,
} from '../../../../ContentEdit/ContentEditSlice';
import CreditConfirmationModal from './CreditConfirmationModal';
import InProgressJobModal from './InProgressJobModal';
import MoreCreditConfirmationModal from './MoreCreditConfirmationModal';
// import CreditPurchaseModal from './CreditPurchaseModal'; currently unused -- we are holding off on implementing modal flow for launch
import VideoEditLoad from './VideoEditLoad';
import VideoEditResult from './VideoEditResult';
import VideoEditorUI from './VideoEditorUI';
import {
  useVideoMetadata,
  useVideoSelection,
  useVideoUI,
  VideoEditorProvider,
} from './context/VideoEditorContext';
import { BroadcastMessage } from './hooks/useCrossTabSync';
import { useVideoEditorSync } from './hooks/useVideoEditorSync';
import { useVideoStatusPolling } from './hooks/useVideoStatusPolling';
import { CREDIT_COST, useCases } from './utils/constants';
import { handlePurchaseCredits } from './utils/purchaseUtils';
import {
  clearModalSessionStorageState,
  removeVideoEditModalParam,
} from './utils/stateUtils';
import { VideoMetadata } from './utils/videoUtils';

const VideoEditorModalContent = ({
  sendMessage,
}: {
  sendMessage: (message: BroadcastMessage) => void;
}) => {
  const dispatch = useDispatch<ThunkDispatch<ReduxState, unknown, AnyAction>>();
  const isOpen = useSelector(selectVideoEditorModalIsOpen);
  const selectedStockItem = useSelector(selectVideoEditorModalStockItem);

  const creditBalance = useSelector(selectCreditBalance);
  const isSpendingCredits = useSelector(selectIsSpendingCredits);
  const modalState = useSelector(selectModalState);
  const userSubscriptionUniqueId = useSelector(selectUserSubscriptionUniqueId);
  const user = useSelector(selectUser);

  const isPolling = useSelector(selectIsPolling);

  const optIns = useSelector(selectOptIns);
  const [isRiskModalOpen, setIsRiskModalOpen] = React.useState(false);
  const [upgradeModalIsOpen, setUpgradeModalIsOpen] = React.useState(false);
  const [showInProgressJobModal, setShowInProgressJobModal] =
    React.useState(false);

  const {
    videoSrc,
    setVideoSrc,
    setVideoMetadata,
    setIsLoadingMetadata,
    setError,
  } = useVideoMetadata();

  const {
    showCreditConfirmation,
    isGeneratingTaskId,
    promptInputRef,
    referenceImage,
    selectedUseCase,
    defaultPlaceholder,
    setShowCreditConfirmation,
    setIsGeneratingTaskId,
  } = useVideoUI();

  const { selection } = useVideoSelection();

  useEffect(() => {
    // on mount, bind key handler
    const closeOnEscapePressed = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dispatch(closeVideoEditorModal());
      }
    };

    window.addEventListener('keydown', closeOnEscapePressed);
    return () => window.removeEventListener('keydown', closeOnEscapePressed);
  }, []);

  // Update video source when stock item changes
  useEffect(() => {
    if (!selectedStockItem) {
      setVideoSrc(null);
      return;
    }

    // Use the preview URL if available
    if (selectedStockItem.previewUrl) {
      setVideoSrc(selectedStockItem.previewUrl);
    } else {
      setVideoSrc(
        getPreviewUrl(selectedStockItem.id, selectedStockItem.contentClass)
      );
    }
  }, [selectedStockItem, setVideoSrc]);

  // Load video metadata immediately when modal opens
  useEffect(() => {
    if (!isOpen || !videoSrc) {
      setVideoMetadata(null);
      setIsLoadingMetadata(true);
      return;
    }

    const loadMetadata = async () => {
      try {
        setIsLoadingMetadata(true);
        setError(null);

        const video = document.createElement('video');
        video.src = videoSrc;
        video.preload = 'metadata';
        video.muted = true;

        const metadata = await new Promise<VideoMetadata>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Video metadata load timeout'));
          }, 5000);

          video.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout);
            resolve({
              duration: video.duration,
              width: video.videoWidth,
              height: video.videoHeight,
            });
          });

          video.addEventListener('error', () => {
            clearTimeout(timeout);
            reject(new Error('Video failed to load'));
          });
        });

        setVideoMetadata(metadata);
        setIsLoadingMetadata(false);
      } catch (error) {
        console.error('❌ Video metadata loading failed:', error);
        setError(
          error instanceof Error ? error.message : 'Failed to load video'
        );
        setIsLoadingMetadata(false);
      }
    };

    loadMetadata();
  }, [isOpen, videoSrc]);

  const handleGenerateVideo = async (hasOptedIn?: boolean) => {
    if (!optIns.aiContentEdit) {
      setIsRiskModalOpen(!hasOptedIn);
      return;
    }
    if (creditBalance < CREDIT_COST) {
      setUpgradeModalIsOpen(true);
      return;
    }

    // Check for in-progress job
    try {
      const response = await ContentEditAPI.checkVideoEditStatus();
      const result = await response.json();
      const taskStatus = result.data?.runway_task_status;

      // Show modal only for in-progress statuses
      if (
        taskStatus === TaskStatus.RUNNING ||
        taskStatus === TaskStatus.PENDING ||
        taskStatus === TaskStatus.THROTTLED
      ) {
        setShowInProgressJobModal(true);
        return;
      }
    } catch (error) {
      // TODO: handle error
      // continue with generation flow even if check fails
    }

    setShowCreditConfirmation(true);
    Telemetry.increment('ai.contentEdit.buttonClick.generateVideo');
  };

  const handleConfirmCreditSpend = async () => {
    try {
      // Get current prompt value from the input
      const currentPrompt = promptInputRef.current?.getPromptValue() || '';

      // First, generate the video and get the taskId
      setIsGeneratingTaskId(true);
      const response = await ContentEditAPI.generateVideoEdit({
        contentId: selectedStockItem?.contentId ?? null,
        prompt_text: currentPrompt,
        start_time: selection?.startTime || 0,
        duration: selection?.duration || 5,
        image_reference: referenceImage || undefined,
      });
      setIsGeneratingTaskId(false);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Video generation error:', errorData);
        notifyError();
        setIsGeneratingTaskId(false);
        return; // Keep confirmation modal open
      }

      const result = await response.json();
      const taskId = result.data?.results?.task_id;

      if (!taskId) {
        notifyError();
        setIsGeneratingTaskId(false);
        return; // Keep confirmation modal open
      }

      // Only spend credits after successful task ID generation
      const resultAction = await dispatch(
        spendCredits({
          amount: CREDIT_COST,
        })
      );

      // Broadcast updated balance to other tabs (use same source as Redux)
      const newBalance = resultAction.payload?.data?.balance;
      if (typeof newBalance === 'number') {
        sendMessage({
          type: 'CREDITS_UPDATED',
          payload: {
            newBalance,
          },
        });
      }

      // Close confirmation modal and start polling
      setShowCreditConfirmation(false);
      dispatch(startPolling(selectedStockItem));

      // Broadcast to other tabs that a job was submitted
      sendMessage({
        type: 'JOB_SUBMITTED',
        payload: {
          stockItem: selectedStockItem,
          taskId: taskId,
        },
      });
    } catch (error) {
      console.error('Failed to generate video or spend credits:', error);
      notifyError();
    } finally {
      setIsGeneratingTaskId(false);
    }
  };

  const handleCancelCreditSpend = () => {
    setShowCreditConfirmation(false);
  };

  const handleClose = () => {
    dispatch(closeVideoEditorModal());
    clearModalSessionStorageState();
    removeVideoEditModalParam();
    if (modalState === 'result') {
      dispatch(resetModalState());
    }
  };

  const renderModalContent = () => {
    switch (modalState) {
      case 'edit':
        return <VideoEditorUI onGenerateVideo={handleGenerateVideo} />;
      case 'loading':
        return <VideoEditLoad handleClose={handleClose} />;
      case 'result':
        return (
          <VideoEditResult
            handleClose={handleClose}
            contentId={selectedStockItem?.contentId ?? null}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-1000 text-white p-4">
      {/* Show UI immediately once metadata is loaded */}
      <div className="flex flex-col items-center max-w-2xl h-full px-6 pb-6 rounded-3xl bg-gray-900 overflow-y-auto">
        <div className="sticky top-0 z-10 pt-6 bg-gray-900 w-full flex justify-between gap-2 items-top">
          <img
            className="h-8 w-8"
            src={`${__ASSETS_COMMON_IMAGES_URL__}/logos/storyblocks-icon.svg`}
            alt="Storyblocks Logo"
          />
          <div className="flex flex-col items-center justify-center">
            <Text fontSize="2xl" className="m-0">
              AI Video Editing
            </Text>
            <img
              className="w-28"
              src={`${__ASSETS_COMMON_IMAGES_URL__}/logos/partners/runway.svg`}
              alt="Powered by Runway Logo"
            />
          </div>
          <button className="flex" onClick={handleClose}>
            <Close className="fill-current h-6 w-6 text-white" />
          </button>
        </div>
        {renderModalContent()}

        <AiRiskModal
          isOpen={isRiskModalOpen}
          onClose={() => setIsRiskModalOpen(false)}
          onSuccess={handleGenerateVideo}
        />
        <InProgressJobModal
          isOpen={showInProgressJobModal}
          onClose={() => {
            window.location.reload();
          }}
        />
        <CreditConfirmationModal
          isOpen={showCreditConfirmation}
          creditCost={CREDIT_COST}
          currentBalance={creditBalance}
          onConfirm={handleConfirmCreditSpend}
          onCancel={handleCancelCreditSpend}
          isLoading={isSpendingCredits || isGeneratingTaskId || isPolling}
        />
        <MoreCreditConfirmationModal
          isOpen={upgradeModalIsOpen}
          onConfirm={() =>
            handlePurchaseCredits({
              selectedStockItem,
              selection,
              selectedUseCase,
              defaultPlaceholder,
              promptText: promptInputRef.current?.getPromptValue() || '',
              planId: userSubscriptionUniqueId,
            })
          }
          onCancel={() => setUpgradeModalIsOpen(false)}
        />
      </div>
    </div>
  );
};

const VideoEditorModal = () => {
  const isOpen = useSelector(selectVideoEditorModalIsOpen);
  const shouldShowAIEditFeatures = useSelector(selectShouldShowAIEditFeatures);
  const contentEditSegment = useSelector(
    selectContentEditSegment
  ) as ContentEditSegment;
  const isPolling = useSelector(selectIsPolling);
  const user = useSelector(selectUser);

  // Cross-tab synchronization - handles all message types and Redux integration
  const { sendMessage } = useVideoEditorSync();

  // Initiates polling once generation starts and broadcasts success/failure to other tabs
  useVideoStatusPolling(sendMessage);

  // Only show modal for valid segments (hide for disabled)
  const shouldShowModal =
    isOpen && shouldShowAIEditFeatures && contentEditSegment !== 'disabled';

  useEffect(() => {
    // after job starts, bind beacon that notifies server if user leaves (for sending email)
    const sendQueueStatusBeacon = () => {
      let beacon = false;
      if (navigator.sendBeacon) {
        const url = new URL(
          '/api/video/generate-edit/queue-status-check-job',
          location.origin
        );
        url.searchParams.set('userId', String(user.id));

        try {
          beacon = navigator.sendBeacon(url.toString());
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('error sending beacon', err);
          return beacon;
        }
      }

      return beacon;
    };

    if (isPolling) {
      window.addEventListener('beforeunload', sendQueueStatusBeacon);
    }

    return () => {
      window.removeEventListener('beforeunload', sendQueueStatusBeacon);
    };
  }, [isPolling]);

  if (!shouldShowModal) return null;

  return (
    <VideoEditorProvider initialUseCase={useCases[0]}>
      <VideoEditorModalContent sendMessage={sendMessage} />
    </VideoEditorProvider>
  );
};

export default VideoEditorModal;
